import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetLmsTrainingCatalogByIdQuery } from '../../../../api';
import toastService from '../../../../utils/toastService';
import TrainingCatalogFormFields from './component/TrainingCatalogFormFields';
import TrainingCatalogDetailTabs from './trainingCatalogDetailTabs/TrainingCatalogDetailTabs';
import TrainingCatalogSummary from '../components/TrainingCatalogSummary';
import useTrainingCatalogMutations from './hook/useTrainingCatalogMutations';
import { useLmsPermission } from '../../hooks/useLmsPermission';
import { LMS } from '../../../../constants/permissionCodes';

const emptyFormData = {
    vehicleModel: null,
    trainingType: null,
    trainingCapacity: null,
    trainingLanguage: '',
    trainingLocation: null,
    trainingDay: '',
    trainingDuration: null,
    preExam: 0,
    isActive: 1
};

/**
 * Eğitim Kataloğu ekleme/düzenleme sayfası. Eskiden `TrainingCatalogForm.jsx` bir modal'dı ve detay
 * (eğitmenler / içerik-kazanım / ön koşullar) tablodaki ayrı bir ikonla açılan başka bir dialog'du
 * (`TrainingDetailDialog.jsx`); ikisi artık tek bir sayfada birleşti — üstte form alanları, altta
 * (yalnızca düzenleme modunda) detay sekmeleri. Yapı, garanti talepleri düzenleme sayfasıyla
 * (`warranty-management/warranty-requests/warranty-claims/createEditForm/WarrantyClaimsCreateEdit.jsx`)
 * aynı desen: `useParams`'tan `id`, yoksa ekleme modu; route'lar `training-catalog/create` ve
 * `training-catalog/:id` (bkz. `App.jsx`).
 *
 * "Eğitim Kodu" backend tarafından otomatik üretilir ve bir daha güncellenmez (doc §3.1 —
 * `MODELKODU-TİPKODU`, örn. `EJEST-UE`). Düzenleme modunda gerçek değer sayfa başlığında gösterilir;
 * ekleme modunda henüz kayıt yokken aynı kalıp (`vehicleModel-trainingType`) seçilen iki dropdown'un
 * KODLARINDAN canlı önizleme olarak üretilir — kaydedilen değeri her zaman backend üretir, bu yalnızca
 * bir önizlemedir.
 */
const TrainingCatalogCreateEdit = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const { data, isLoading: isLoadingDetail, isFetching: isFetchingDetail } = useGetLmsTrainingCatalogByIdQuery(id, { skip: !isEdit });
    const { saveTraining, deleteTraining, isCreating, isUpdating, isDeleting } = useTrainingCatalogMutations();

    const [formData, setFormData] = useState(emptyFormData);
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (!isEdit) {
            setFormData(emptyFormData);
            setLocalSubmitted(false);
            return;
        }
        if (!data) return;
        setFormData({
            vehicleModel: data.vehicleModel ?? null,
            trainingType: data.trainingType ?? null,
            trainingCapacity: data.trainingCapacity ?? null,
            trainingLanguage: data.trainingLanguage ?? '',
            trainingLocation: data.trainingLocation ?? null,
            trainingDay: data.trainingDay ?? '',
            trainingDuration: data.trainingDuration ?? null,
            preExam: data.preExam ?? 0,
            isActive: data.isActive !== undefined && data.isActive !== null ? Number(data.isActive) : 1
        });
        setLocalSubmitted(false);
    }, [isEdit, data]);

    // Doc §3.1: `trainingCode` kayıttan sonra güncellenmez — düzenlemede her zaman backend'in
    // ürettiği sabit değer gösterilir; yalnızca ekleme modunda, henüz kod yokken canlı önizleme üretilir.
    const codePreview = isEdit ? data?.trainingCode || '' : formData.vehicleModel && formData.trainingType ? `${formData.vehicleModel}-${formData.trainingType}` : '';

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        if (field === 'trainingCapacity') return !(Number(formData.trainingCapacity) > 0);
        if (field === 'trainingDuration') return !(Number(formData.trainingDuration) > 0);
        if (typeof formData[field] === 'string') return !formData[field].trim();
        return formData[field] == null;
    };

    const handleSave = async () => {
        setLocalSubmitted(true);

        const requiredFields = ['vehicleModel', 'trainingType', 'trainingLanguage', 'trainingLocation', 'trainingDay'];
        const missingRequired = requiredFields.some((field) => {
            const value = formData[field];
            return typeof value === 'string' ? !value.trim() : value == null;
        });
        const invalidNumbers = !(Number(formData.trainingCapacity) > 0) || !(Number(formData.trainingDuration) > 0);

        if (missingRequired || invalidNumbers) {
            toastService.error(th('trainingCatalog.formValidationMessage'), th('trainingCatalog.missingField'));
            return;
        }

        const payload = {
            vehicleModel: formData.vehicleModel,
            trainingType: formData.trainingType,
            trainingCapacity: Number(formData.trainingCapacity),
            trainingLanguage: formData.trainingLanguage.trim(),
            trainingLocation: formData.trainingLocation,
            trainingDay: formData.trainingDay.trim(),
            trainingDuration: Number(formData.trainingDuration),
            preExam: Number(formData.preExam),
            isActive: Number(formData.isActive)
        };
        if (isEdit) payload.id = Number(id);

        const result = await saveTraining(payload, isEdit);
        if (result.success && !isEdit && result.newId) {
            navigate(`/learning-management-system/training-catalog/${result.newId}`, { replace: true });
        }
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('trainingCatalog.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteTraining(id)
        });
    };

    if (isEdit && isLoadingDetail) {
        return (
            <div className="card flex justify-content-center p-5">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="card">
            <ConfirmDialog />

            <div className="flex align-items-center gap-2 mb-4">
                <Button icon="pi pi-arrow-left" rounded text onClick={() => navigate('/learning-management-system/training-catalog')} title={th('common.back')} />
                <h1 className="m-0">{isEdit ? th('trainingCatalog.detailPrefix') : th('trainingCatalog.newButton')}</h1>
            </div>

            <TrainingCatalogFormFields formData={formData} setFormData={setFormData} isFieldInvalid={isFieldInvalid} isEdit={isEdit} codePreview={codePreview} />

            {isEdit && data && <TrainingCatalogSummary data={data} />}

            <div className="flex justify-content-end gap-2 mt-3">
                {isEdit && can(LMS.TRAINING_DELETE) && (
                    <Button
                        label={th('common.delete')}
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        onClick={handleDelete}
                        loading={isDeleting}
                        disabled={!!data?.hasPlanning}
                        tooltip={data?.hasPlanning ? th('trainingCatalog.deleteDisabledHasPlanning') : undefined}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    />
                )}
                {(!isEdit || can(LMS.TRAINING_UPDATE)) && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isCreating || isUpdating || (isEdit && isFetchingDetail)} />}
            </div>

            {isEdit && (
                <>
                    <div className="my-4 border-top-1 border-300" />
                    <TrainingCatalogDetailTabs trainingId={data?.id} instructorsCount={data?.instructorsCount} readOnly={!can([LMS.TRAINING_CREATE, LMS.TRAINING_UPDATE])} />
                </>
            )}
        </div>
    );
};

export default TrainingCatalogCreateEdit;
